import React, { useState, useEffect } from "react";
import { Dialog } from "../containers/Dialog.tsx";
import { Button } from "../widgets/Button.tsx";
import { TextInput } from "../inputs/TextInput.tsx";
import {
  Exercise,
  CreateExerciseRequest,
  MUSCLE_GROUP_LABELS,
  MuscleGroup,
  MuscleGroupWeight,
} from "../../api/types.ts";
import { isNotNullish } from "../../util/typeGuards.ts";
import { Dropdown } from "../widgets/Dropdown.tsx";
import { DropdownItem } from "../widgets/DropdownItem.tsx";
import { FaPlus, FaTimes } from "react-icons/fa";
import { getSupportedMuscleGroups } from "../../util/muscleGroupIcons.ts";
import { Select } from "../inputs/Select.tsx";
import { useDialog } from "../DialogManager.tsx";
import { Banner } from "../widgets/Banner.tsx";
import { useCreateExercise, usePatchExercise } from "../../api/hooks.ts";
import { TextArea } from "../inputs/TextArea.tsx";

interface ExerciseValidationErrors {
  name?: string;
  primary_muscle_groups?: string;
  secondary_muscle_groups?: string;
  start_resistance?: string;
  default_resistance_unit?: string;
  efficiency_factor?: string;
}

interface ExerciseDialogProps {
  open: boolean;
  closeDialog: () => void;
  exercise?: Exercise;
  preferredResistanceUnit: "kg" | "lbs";
}

const ExerciseDialog: React.FC<ExerciseDialogProps> = ({
  open,
  closeDialog,
  exercise,
  preferredResistanceUnit,
}) => {
  const { setError } = useDialog();

  const patchExerciseMutation = usePatchExercise();
  const createExerciseMutation = useCreateExercise();

  const [exerciseName, setExerciseName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [primaryMuscleGroups, setPrimaryMuscleGroups] = useState<
    MuscleGroupWeight[]
  >([]);
  const [secondaryMuscleGroups, setSecondaryMuscleGroups] = useState<
    MuscleGroupWeight[]
  >([]);
  const [startResistance, setStartResistance] = useState<string>("0");
  const [defaultResistanceUnit, setDefaultResistanceUnit] = useState<string>(
    preferredResistanceUnit,
  );
  const [efficiencyFactor, setEfficiencyFactor] = useState<string>("100");
  const [validationErrors, setValidationErrors] =
    useState<ExerciseValidationErrors>({});

  const supportedMuscleGroups = getSupportedMuscleGroups();
  const availableMuscleGroups = supportedMuscleGroups.filter(
    (mg) =>
      !primaryMuscleGroups.map((muscle) => muscle.muscle_group).includes(mg),
  );

  // Update form when exercise prop changes
  useEffect(() => {
    if (isNotNullish(exercise)) {
      setExerciseName(exercise.name);
      setDescription(exercise.description || "");
      setPrimaryMuscleGroups(exercise.primary_muscle_groups);
      setEfficiencyFactor((exercise.efficiency_factor * 100).toString());
      setStartResistance(exercise.start_resistance.toString());
      setDefaultResistanceUnit(exercise.default_resistance_unit);
    } else {
      setExerciseName("");
      setDescription("");
      setPrimaryMuscleGroups([]);
      setEfficiencyFactor("100");
      setStartResistance("0");
      setDefaultResistanceUnit(preferredResistanceUnit);
    }
  }, [exercise, preferredResistanceUnit]);

  useEffect(() => {
    setValidationErrors((current) => ({ ...current, name: undefined }));
  }, [exerciseName]);

  useEffect(() => {
    setValidationErrors((current) => ({
      ...current,
      efficiency_factor: undefined,
    }));
  }, [efficiencyFactor]);

  const validateInputs = ({
    name,
    primaryMuscleGroups,
    secondaryMuscleGroups,
    startResistance,
    defaultResistanceUnit,
    efficiency,
  }: {
    name: string;
    primaryMuscleGroups: MuscleGroupWeight[];
    secondaryMuscleGroups: MuscleGroupWeight[];
    startResistance: string;
    defaultResistanceUnit: string;
    efficiency: string;
  }) => {
    const errors: ExerciseValidationErrors = {};

    if (name.trim().length === 0) {
      errors.name = "Exercise name is required";
    } else if (name.trim().length < 3) {
      errors.name = "Exercise name must be at least 3 characters";
    } else if (name.length > 50) {
      errors.name = "Exercise name must be 50 characters or less";
    }

    if (primaryMuscleGroups.length === 0) {
      errors.primary_muscle_groups =
        "At least one primary muscle group is required";
    } else if (primaryMuscleGroups.length > 5) {
      errors.primary_muscle_groups =
        "No more than 5 primary muscle groups allowed";
    }

    if (secondaryMuscleGroups.length > 5) {
      errors.secondary_muscle_groups =
        "No more than 5 secondary muscle groups allowed";
    } else if (
      secondaryMuscleGroups.some((mg) =>
        primaryMuscleGroups
          .map((muscle) => muscle.muscle_group)
          .includes(mg.muscle_group),
      )
    ) {
      errors.secondary_muscle_groups =
        "Secondary muscle groups must be different from primary muscle groups";
    }

    const startResNum = Number(startResistance);
    if (isNaN(startResNum) || startResNum < 0) {
      errors.start_resistance =
        "Start resistance must be a non-negative number";
    }

    if (defaultResistanceUnit !== "kg" && defaultResistanceUnit !== "lbs") {
      errors.default_resistance_unit =
        "Default resistance unit must be either 'kg' or 'lbs'";
    }

    const efficiencyNum = Number(efficiency);
    if (isNaN(efficiencyNum) || efficiencyNum < 1 || efficiencyNum > 100) {
      errors.efficiency_factor = "Efficiency factor must be between 1 and 100";
    }

    return errors;
  };

  const handleSave = () => {
    const errors = validateInputs({
      name: exerciseName,
      primaryMuscleGroups,
      secondaryMuscleGroups,
      startResistance,
      defaultResistanceUnit,
      efficiency: efficiencyFactor,
    });

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const exerciseData: CreateExerciseRequest = {
      name: exerciseName.trim(),
      ...(description.trim() && { description: description.trim() }),
      primary_muscle_groups: primaryMuscleGroups as MuscleGroupWeight[],
      secondary_muscle_groups: secondaryMuscleGroups as MuscleGroupWeight[],
      efficiency_factor: Number((Number(efficiencyFactor) / 100).toFixed(3)),
      start_resistance: Number(startResistance),
      default_resistance_unit: defaultResistanceUnit as "kg" | "lbs",
    };

    if (exercise) {
      patchExerciseMutation.mutate(
        { id: exercise.id, exercise: exerciseData },
        { onSuccess: handleClose, onError: (e) => setError(e) },
      );
    } else {
      createExerciseMutation.mutate(exerciseData, {
        onSuccess: handleClose,
        onError: (e) => setError(e),
      });
    }
  };

  const handleClose = () => {
    setExerciseName("");
    setDescription("");
    setPrimaryMuscleGroups([]);
    setEfficiencyFactor("100");
    setDefaultResistanceUnit(preferredResistanceUnit);
    setValidationErrors({});
    closeDialog();
  };

  useEffect(() => {
    const nbrPrimary = primaryMuscleGroups.length;

    if (nbrPrimary === 0) {
      return;
    }

    const nbrSecondary = secondaryMuscleGroups.length;

    function roundToNearestHalfPoint(nbr: number) {
      return 0.005 * Math.floor(nbr / 0.005);
    }

    if (nbrSecondary === 0) {
      setPrimaryMuscleGroups((prev) =>
        prev.map((muscle) => ({
          ...muscle,
          weight: 0.005 * Math.floor(1.0 / nbrPrimary / 0.005),
        })),
      );
    } else {
      // Try to compute default muscle group weights that makes sense.
      // Secondary muscle group weight should never exceed primary but also not be unreasonably low.
      const secondaryProportion =
        0.5 / (1.5 + Math.exp(nbrPrimary / 2 - nbrSecondary));

      setPrimaryMuscleGroups((prev) =>
        prev.map((muscle) => ({
          ...muscle,
          weight: roundToNearestHalfPoint(
            (1.0 - secondaryProportion) / nbrPrimary,
          ),
        })),
      );
      setSecondaryMuscleGroups((prev) =>
        prev.map((muscle) => ({
          ...muscle,
          weight: roundToNearestHalfPoint(secondaryProportion / nbrSecondary),
        })),
      );
    }
  }, [primaryMuscleGroups.length, secondaryMuscleGroups.length]);

  const handleAddPrimaryMuscleGroup = (muscleGroup: string) => {
    if (
      !primaryMuscleGroups
        .map((muscle) => muscle.muscle_group)
        .includes(muscleGroup as MuscleGroup)
    ) {
      setPrimaryMuscleGroups([
        ...primaryMuscleGroups,
        { muscle_group: muscleGroup as MuscleGroup, weight: 0.15 },
      ]);
    }
  };

  const handleRemovePrimaryMuscleGroup = (muscleGroup: string) => {
    setPrimaryMuscleGroups(
      primaryMuscleGroups.filter((mg) => mg.muscle_group !== muscleGroup),
    );
  };

  const handleAddSecondaryMuscleGroup = (muscleGroup: string) => {
    if (
      !secondaryMuscleGroups
        .map((muscle) => muscle.muscle_group)
        .includes(muscleGroup as MuscleGroup)
    ) {
      setSecondaryMuscleGroups([
        ...secondaryMuscleGroups,
        { muscle_group: muscleGroup as MuscleGroup, weight: 0.05 },
      ]);
    }
  };

  const handleRemoveSecondaryMuscleGroup = (muscleGroup: string) => {
    setSecondaryMuscleGroups(
      secondaryMuscleGroups.filter((mg) => mg.muscle_group !== muscleGroup),
    );
  };

  const { openDialog } = useDialog();

  const handleAdjustWeightsClick = () => {
    openDialog("adjust-muscle-group-weights", {
      primaryMuscleGroups: primaryMuscleGroups,
      secondaryMuscleGroups: secondaryMuscleGroups,
      onSave: (
        primary: MuscleGroupWeight[],
        secondary: MuscleGroupWeight[],
      ) => {
        setPrimaryMuscleGroups(primary);
        setSecondaryMuscleGroups(secondary);
      },
      exercise: exercise,
    });
  };

  const formatMuscleGroupLabel = (muscleGroup: string): string => {
    return MUSCLE_GROUP_LABELS[muscleGroup as MuscleGroup] || muscleGroup;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={
        isNotNullish(exercise) ? `Edit ${exercise.name}` : "Add an Exercise"
      }
    >
      <Dialog.Content>
        {exercise ? (
          <Banner
            visible
            variant="info"
            title="Notice"
            body="Routines that reference this exercise will take on the following changes. Workouts created with this exercise are however not affected by any changes."
          />
        ) : (
          <p className="mb-4">
            An exercise refers to a specific movement pattern done with or
            without gym equipment. Specify between one and five primary and up
            to five secondary muscle groups to better categorize the exercise.
          </p>
        )}
        <TextInput
          label="Exercise Name"
          required
          value={exerciseName}
          onChange={(e) => setExerciseName(e.target.value)}
          placeholder="e.g., Bench Press"
          errorText={validationErrors.name}
        />
        <div className="flex flex-col gap-1">
          <label className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional notes about this exercise..."
            className="p-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-info resize-vertical min-h-[80px]"
            maxLength={500}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-2 items-center">
            <label className="block text-sm font-medium text-gray-700">
              Primary Muscle Groups<span className="text-red-500 ml-1">*</span>
            </label>
            <Dropdown
              trigger={
                <button
                  id="button-primary-muscle-groups"
                  type="button"
                  className="items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-secondary hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={availableMuscleGroups.length === 0}
                >
                  <FaPlus size={12} />
                </button>
              }
              alignment="left"
            >
              {availableMuscleGroups.map((muscleGroup) => (
                <DropdownItem
                  key={muscleGroup}
                  onClick={() => handleAddPrimaryMuscleGroup(muscleGroup)}
                >
                  {formatMuscleGroupLabel(muscleGroup)}
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
          <p className="mb-2 text-sm">
            The primary muscle groups are the main focus of the exercise.
          </p>
          <div className="flex flex-wrap gap-2 mb-2">
            {primaryMuscleGroups.length > 0 ? (
              primaryMuscleGroups.map((muscleGroup) => (
                <button
                  key={muscleGroup.muscle_group}
                  type="button"
                  onClick={() =>
                    handleRemovePrimaryMuscleGroup(muscleGroup.muscle_group)
                  }
                  className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-2 rounded-full text-sm hover:bg-red-100 hover:text-red-700 transition-colors cursor-pointer"
                  aria-label={`Remove ${formatMuscleGroupLabel(muscleGroup.muscle_group)}`}
                >
                  <span>
                    {formatMuscleGroupLabel(muscleGroup.muscle_group)}
                  </span>
                  <span className="text-xs italic">
                    ({Number((muscleGroup.weight * 100).toFixed(1))}%)
                  </span>
                  <FaTimes size={12} className="flex-shrink-0" />
                </button>
              ))
            ) : (
              <span className="text-sm text-gray-500 italic">
                No muscle groups selected yet.
              </span>
            )}
            {validationErrors.primary_muscle_groups && (
              <div className="w-full">
                <p className="text-xs text-danger mt-1">
                  {validationErrors.primary_muscle_groups}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-2 items-center">
            <label className="block text-sm font-medium text-gray-700">
              Secondary Muscle Groups
            </label>
            <Dropdown
              trigger={
                <button
                  id="button-secondary-muscle-groups"
                  type="button"
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-secondary hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={availableMuscleGroups.length === 0}
                >
                  <FaPlus size={12} />
                </button>
              }
              alignment="left"
            >
              {availableMuscleGroups.map((muscleGroup) => (
                <DropdownItem
                  key={muscleGroup}
                  onClick={() => handleAddSecondaryMuscleGroup(muscleGroup)}
                >
                  {formatMuscleGroupLabel(muscleGroup)}
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
          <p className="mb-2 text-sm">
            The secondary muscle groups are the supporting muscle groups of the
            exercise.
          </p>
          <div className="flex flex-wrap gap-2 mb-2">
            {secondaryMuscleGroups.length > 0 ? (
              secondaryMuscleGroups.map((muscleGroup) => (
                <button
                  key={muscleGroup.muscle_group}
                  type="button"
                  onClick={() =>
                    handleRemoveSecondaryMuscleGroup(muscleGroup.muscle_group)
                  }
                  className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-2 rounded-full text-sm hover:bg-red-100 hover:text-red-700 transition-colors cursor-pointer"
                  aria-label={`Remove ${formatMuscleGroupLabel(muscleGroup.muscle_group)}`}
                >
                  <span>
                    {formatMuscleGroupLabel(muscleGroup.muscle_group)}
                  </span>
                  <span className="text-xs italic">
                    ({Number((muscleGroup.weight * 100).toFixed(1))}%)
                  </span>
                  <FaTimes size={12} className="flex-shrink-0" />
                </button>
              ))
            ) : (
              <span className="text-sm text-gray-500 italic">
                No muscle groups selected yet.
              </span>
            )}
          </div>
          {validationErrors.secondary_muscle_groups && (
            <div className="w-full">
              <p className="text-xs text-danger mt-1">
                {validationErrors.secondary_muscle_groups}
              </p>
            </div>
          )}
        </div>
        {primaryMuscleGroups.length > 0 && (
          <div>
            <p className="mb-4 text-sm">
              You may adjust the weight for each muscle group to indicate the
              relative emphasis of the exercise on that muscle group. The
              weights will be used to estimate your performance in each recorded
              workout using this exercise.
            </p>
            <Button
              onClick={handleAdjustWeightsClick}
              className="text-sm rounded-2xl mb-2"
              variant="neutral"
            >
              Adjust Muscle Group Weights
            </Button>
          </div>
        )}
        <Select
          label="Default Resistance Unit"
          required
          value={defaultResistanceUnit}
          options={["kg", "lbs"]}
          onChange={(value) => setDefaultResistanceUnit(value)}
        />
        <div>
          <TextInput
            label="Start Resistance"
            required
            type="number"
            value={startResistance}
            infoText="The start resistance will be added to the total resistance in
              recorded sets of this exercise. The efficiency factor does not
              apply to the start resistance."
            onChange={(e) => setStartResistance(e.target.value)}
            rightElement={<span>{defaultResistanceUnit}</span>}
            placeholder="0"
            errorText={validationErrors.start_resistance}
            min="0"
          />
        </div>
        <div>
          <TextInput
            label="Efficiency Factor"
            required
            type="number"
            value={efficiencyFactor}
            infoText="Some free-weight gym equipment place only a portion of the added
              resistance directly on the targeted muscle groups. The
              efficiency factor yields the effective resistance that
              directly tagrets the muscle groups."
            onChange={(e) => setEfficiencyFactor(e.target.value)}
            placeholder="100"
            errorText={validationErrors.efficiency_factor}
            min="1"
            max="100"
            rightElement="%"
          />
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          onClick={handleSave}
          loading={
            patchExerciseMutation.isPending || createExerciseMutation.isPending
          }
        >
          Save
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export { ExerciseDialog };
